import { withHydrate } from '@relay/react/hooks';
import { Device, from } from '@helpers/media';
import { isSSR } from '@helpers/ssr';
import withRedux from '@helpers/withRedux';
import { withMotionMax } from '@hoc/withMotionMax';
import useMedia from '@hooks/useMedia';
import useSeoText from '@hooks/useSeoText';
import CarouselProvider from '@stories/Components/Carousel/CarouselProvider/CarouselProvider';
import Connected from '@stories/Components/Carousel/Connected/Connected';
import ProductCompare from '@stories/Components/Products/ProductCompare/ProductCompare';
import ProductList from '@stories/Components/Products/ProductList/ProductList.hybrid';
import { ProductListingFiltersStyles } from '@stories/Components/Products/ProductListingFilters/ProductListingFilters.styles';
import CarouselButton from '@stories/Elements/Media/CarouselButton/CarouselButton';
import { Column } from '@stories/Settings/Grids/Grid.styles';
import { MotionConfig } from 'framer-motion';
import React, { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Route, BrowserRouter as Router, StaticRouter } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import ProductListHeader from '../ProductListHeader/ProductListHeader';
import { ProductListingStyles } from './ProductListing.styles';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const ProductListing = props => {
  const fullTextRef = useRef(null);
  const wordLimit = useMedia([from(Device.DesktopSmall), from(Device.TabletLarge), from(Device.MobileLarge)], [80, 50, 30], 20);
  const {
    text: truncatedSubtitle,
    hasTruncated: showReadMore
  } = useSeoText(props.categoryDescription, wordLimit);
  const [childCategories, setChildCategories] = React.useState();
  const slidesToShow = useMedia([from(Device.DesktopSmall), from(Device.TabletLarge), from(Device.Tablet)], [10, 6, 5], 3);
  const {
    items,
    showCompare
  } = useSelector(x => x.compare);
  const productListHeaderMemo = useMemo(() => {
    return /*#__PURE__*/_jsx(ProductListHeader, {
      title: props.categoryTitle,
      description: truncatedSubtitle,
      backText: props.categoryBackText,
      backUrl: props.categoryBackUrl,
      inView: true,
      background: "white",
      showReadMore: showReadMore,
      readMoreText: props.translations['custom.read-more'],
      readMoreOnClick: () => {
        if (fullTextRef.current) {
          fullTextRef.current.scrollIntoView({
            behavior: 'smooth'
          });
        }
      }
    });
  }, [truncatedSubtitle]);
  const categoryFiltersMemo = useMemo(() => {
    if (!childCategories || childCategories.filterOptions.length === 0) {
      return null;
    }
    return /*#__PURE__*/_jsx(ProductListingStyles.CategoryFiltersWrapper, {
      children: /*#__PURE__*/_jsx(Column.Full, {
        children: /*#__PURE__*/_jsx(MotionConfig, {
          transition: {
            duration: 0.45,
            ease: 'easeOut'
          },
          children: /*#__PURE__*/_jsx(CarouselProvider, {
            count: childCategories.filterOptions.length,
            paddingOffset: 0,
            pageChildren: 1,
            children: /*#__PURE__*/_jsx(Connected, {
              children: carousel => /*#__PURE__*/_jsxs(_Fragment, {
                children: [/*#__PURE__*/_jsx(ProductListingStyles.CategoryFilters, {
                  children: /*#__PURE__*/_jsx(ProductListingStyles.Track, {
                    "data-center": childCategories.filterOptions.length > slidesToShow,
                    trackMutations: true,
                    enableCarousel: true,
                    children: childCategories.filterOptions.map(category => /*#__PURE__*/_jsx(ProductListingStyles.CategoryFilterItemWrapper, {
                      onClick: () => {
                        window.location.href = category.value;
                      },
                      children: /*#__PURE__*/_jsxs(ProductListingStyles.CategoryFilterItem, {
                        href: category.value,
                        children: [/*#__PURE__*/_jsx(ProductListingStyles.CategoryFilterItemImageContainer, {
                          children: /*#__PURE__*/_jsx(ProductListingStyles.CategoryFilterItemImage, {
                            src: category.listingImage,
                            role: "presentation"
                          })
                        }), /*#__PURE__*/_jsx(ProductListingStyles.CategoryFilterItemTitle, {
                          children: category.name
                        })]
                      })
                    }, category.name))
                  })
                }), childCategories.filterOptions.length > slidesToShow && /*#__PURE__*/_jsxs(ProductListingStyles.ControlsContainer, {
                  children: [/*#__PURE__*/_jsx(ProductListingStyles.LineContainer, {
                    progress: (carousel.activeIndex + slidesToShow) / childCategories.filterOptions.length * 100
                  }), /*#__PURE__*/_jsxs(ProductListingStyles.Controls, {
                    children: [/*#__PURE__*/_jsx(CarouselButton, {
                      direction: "left",
                      disabled: carousel.activeIndex === 0,
                      theme: "red",
                      hideLine: true,
                      onClick: event => {
                        event.preventDefault();
                        carousel?.goToPrev();
                      }
                    }), /*#__PURE__*/_jsx(CarouselButton, {
                      direction: "right",
                      theme: "red",
                      hideLine: true,
                      disabled: carousel.activeIndex + slidesToShow >= childCategories.filterOptions?.length,
                      onClick: event => {
                        event.preventDefault();
                        carousel?.goToNext();
                      }
                    })]
                  })]
                })]
              })
            })
          })
        })
      })
    });
  }, [childCategories, slidesToShow]);
  const productListMemo = useMemo(() => {
    return /*#__PURE__*/_jsxs(ProductListingStyles.Wrapper, {
      children: [/*#__PURE__*/_jsx(ProductListingFiltersStyles.FiltersElement, {
        "data-compare-open": showCompare && items.length > 0,
        "data-filters-bar": "true"
      }), /*#__PURE__*/_jsx(ProductList, {
        ...props,
        showCards: true,
        productListGuids: null,
        productListGuid: null,
        groupByCategory: false,
        includeAllCategories: false,
        listingType: props.listingType,
        promoCards: [],
        setChildCategories: setChildCategories
      })]
    });
  }, [props, showCompare, items.length]);
  const FooterDescriptionMemo = useMemo(() => {
    if (!showReadMore) {
      return null;
    }
    return /*#__PURE__*/_jsx(ProductListingStyles.FooterDescriptionContainer, {
      children: /*#__PURE__*/_jsx(ProductListingStyles.FooterDescriptionInner, {
        children: /*#__PURE__*/_jsx(ProductListingStyles.FooterDescription, {
          ref: fullTextRef,
          dangerouslySetInnerHTML: {
            __html: props.categoryDescription || ''
          }
        })
      })
    });
  }, [showReadMore, props.categoryDescription]);
  const compareSectionMemo = useMemo(() => {
    if (!props.enableCompare) {
      return null;
    }
    return /*#__PURE__*/_jsx(ProductCompare, {
      pageContext: props.pageContext,
      translations: props.translations,
      showUnpublished: false
    });
  }, [props.enableCompare, props.pageContext, props.translations]);

  // Memoize the children to re-render correctly when the state, i.e truncatedSubtitle changes
  const Children = /*#__PURE__*/_jsxs(_Fragment, {
    children: [productListHeaderMemo, categoryFiltersMemo, productListMemo, FooterDescriptionMemo, compareSectionMemo]
  });
  if (isSSR) {
    return /*#__PURE__*/_jsx(StaticRouter, {
      children: /*#__PURE__*/_jsx(QueryParamProvider, {
        ReactRouterRoute: Route,
        children: Children
      })
    });
  }
  return /*#__PURE__*/_jsx(Router, {
    children: /*#__PURE__*/_jsx(QueryParamProvider, {
      ReactRouterRoute: Route,
      children: Children
    })
  });
};
export default withHydrate(withRedux(withMotionMax(ProductListing)), 'ProductListing', 'hybrid');
